/**
 * Doctrine as a player-shaped record (56.7, decision 81): the sliders a faction's doctrine carries, resolved
 * into the numbers the stand-in commander and the battle read — the way a kit resolves into modifiers.
 * First slider: **caution**, which sets the sneak decision (62.14). Pure.
 */
import type { Doctrine } from './types';

/** The caution a doctrine without the slider has: today's rule (sneak when outnumbered by what we know is there). */
export const DEFAULT_CAUTION = 0.5;

/** The doctrine's caution, 0 (never sneak) … 1 (always sneak), clamped; the default where unset. */
export function cautionOf(d: Pick<Doctrine, 'caution'>): number {
  const c = d.caution ?? DEFAULT_CAUTION;
  return Math.min(1, Math.max(0, c));
}

/**
 * The commander sneaks at a garrisoned enemy system when the enemy it knows is there is stronger than
 * `sneakOdds × our strength`. Caution 0.5 gives 1 (sneak when outnumbered, today's rule); caution 0 gives
 * Infinity (never); caution 1 gives 0 (always). Monotone in between: (1 − c) / c.
 */
export function sneakOdds(d: Pick<Doctrine, 'caution'>): number {
  const c = cautionOf(d);
  if (c <= 0) return Infinity;
  if (c >= 1) return 0;
  return (1 - c) / c;
}

/** 56.7: the share of its starting strength a side spends before disengaging — from `commitment`, else today's reading of the round cap. */
export function withdrawAtOf(d: Pick<Doctrine, 'commitment' | 'rounds'>): number {
  if (d.commitment !== undefined) return 0.3 + 0.7 * Math.min(1, Math.max(0, d.commitment));
  return d.rounds >= 20 ? 1 : Math.max(0.3, d.rounds / 20);
}

/** 56.7: the go-loud clock of a sneaking attacker — rounds left when it goes loud — from `sneak`, else today's `goLoudRoundsLeft`. */
export function goLoudOf(d: Pick<Doctrine, 'sneak' | 'rounds' | 'goLoudRoundsLeft'>): number {
  if (d.sneak !== undefined) return Math.round((1 - Math.min(1, Math.max(0, d.sneak))) * d.rounds * 0.5);
  return d.goLoudRoundsLeft;
}

/** The doctrine resolved into the numbers its readers take, the way a kit resolves into modifiers (56.7). */
export interface ResolvedDoctrine { sneakOdds: number; withdrawAt: number; goLoudRoundsLeft: number }
export function resolveDoctrine(d: Doctrine): ResolvedDoctrine {
  return { sneakOdds: sneakOdds(d), withdrawAt: withdrawAtOf(d), goLoudRoundsLeft: goLoudOf(d) };
}

/** Sneak or assault, from the doctrine and the strengths (our living units, the enemy strength we know of at the target). */
export function sneakDecision(d: Pick<Doctrine, 'caution'>, ours: number, theirs: number): 'sneak' | 'assault' {
  const k = sneakOdds(d);
  if (k === Infinity) return 'assault';
  if (k === 0) return 'sneak';
  return theirs > ours * k ? 'sneak' : 'assault';
}
